import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiService } from './api.service';

/**
 * Service responsible for managing event performer summary data and related event/compliance API interactions.
 * It stores and clears activated event data and event data in memory for use across the application, and exposes
 * a collection of HTTP POST methods for retrieving, saving, and updating upcoming events, assigned/activated
 * events, compliance information, child grid compliance, schedule details, user-customer mappings, reassignments,
 * and short-notice events.
 * @service
 * @providedIn root
 *
 * @example
 *   eventPerformerSummaryService.setEventData({ event_id: 123, customer_branch_id: 456 });
 *   eventPerformerSummaryService.getEventData({ event_id: 123, customer_branch_id: 456 });
 *
 * @method setActivatedEventData - Stores the currently activated event details in memory.
 * @method retrieveActivatedEventData - Returns the stored activated event details.
 * @method clearActivatedEventData - Clears the stored activated event details.
 * @method setEventData - Stores the current event data in memory.
 * @method retrieveEventData - Returns the stored event data.
 * @method clearEventData - Clears the stored event data.
 * @method getUpcomingEventData - Fetches upcoming event data from the API.
 * @method getAssignedEventData - Fetches assigned event data from the API.
 * @method getActivatedEventData - Fetches activated event data from the API.
 * @method saveAllActivatedEventData - Saves all activated event data to the API.
 * @method checkAllNotAssignedCompliance - Checks not-assigned compliance records.
 * @method saveAllActivatedEventNewOne - Saves activated event data using the alternate endpoint.
 * @method saveAllActivatedEventNew - Saves activated event data using the primary endpoint.
 * @method getEventData - Retrieves event data from the API.
 * @method saveEventNotApplicable - Marks an event as not applicable.
 * @method getParentEventData - Retrieves parent event data.
 * @method getSubEventData - Retrieves sub-event data.
 * @method getComplianceData - Retrieves compliance data for an event.
 * @method getComplianceVisibilityAndColor - Retrieves compliance visibility and color information.
 * @method getBindInternalCompliance - Retrieves internal compliance binding data.
 * @method addChildGridInternalCompliance - Adds child grid internal compliance data.
 * @method markChildGridEventNotApplicable - Marks a child grid event as not applicable.
 * @method childGridUpdateOrSaveCompliance - Creates or updates child grid compliance data.
 * @method getScheduleDates - Retrieves schedule dates from the API.
 * @method btnClosureCloseComplianceSchedule - Closes a compliance schedule.
 * @method getIntermediateCompliance - Retrieves intermediate compliance data.
 * @method getIntermediateComplianceVisibilityAndColor - Retrieves intermediate compliance visibility and color data.
 * @method intermediateGridEventNotApplicable - Marks an intermediate grid event as not applicable.
 * @method intermeditateChildGridUpdateCompliance - Updates intermediate child grid compliance.
 * @method getAllUserCustomerId - Retrieves customer IDs associated with users.
 * @method saveComplianceList - Saves a compliance list.
 * @method usersReassignCompliance - Reassigns compliance users.
 * @method saveShortNotice - Saves short-notice event details.
 *
 * @note This service does not implement Angular component lifecycle hooks, as it is an injectable service rather than a component.
 */
@Injectable({
  providedIn: 'root',
})
export class EventPerformerSummaryService {
  private apiBaseUrl = environment.API_BASE_URL;
  private eventData: { event_id: number; customer_branch_id: number } | null = null;


  private activatedEventData: { parent_event_id: number; event_scheduled_on_id: number; type: number, customer_branch_id: number, event_description: string } | null = null

  setActivatedEventData(data: { parent_event_id: number; event_scheduled_on_id: number; type: number, customer_branch_id: number, event_description: string }) {
    this.activatedEventData = data;
  }

  retrieveActivatedEventData() {
    return this.activatedEventData;
  }

  clearActivatedEventData() {
    this.activatedEventData = null;
  }

  setEventData(data: { event_id: number; customer_branch_id: number }) {
    this.eventData = data;
  }

  retrieveEventData() {
    return this.eventData;
  }

  clearEventData() {
    this.eventData = null;
  }

  constructor(private api: ApiService) { }

  /**
  * Request upcoming event data from the API, signing the payload and returning the full HTTP response as an Observable.
  * @example
  * etUpcomingEventData({ limit: 10, page: 1 })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload used to compute the X-Payload-HMAC header and sent to the /event/upcoming-event-data endpoint.
  * @returns {{Observable<HttpResponse<any>>}} Returns an Observable that emits the HttpResponse containing the upcoming event data.
  **/
  getUpcomingEventData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/upcoming-event-data`;
    return this.api.post(url, params);
  }

  /**
  * Sends a POST request to the /event/assigned-event-data endpoint with an HMAC header and bearer token, returning the full HTTP response as an observable.
  * @example
  * etAssignedEventData({ eventId: 123 })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Payload object to include in the POST body.
  * @returns {{Observable<HttpResponse<any>>>}} Observable that emits the full HttpResponse containing the server response.
  **/
  getAssignedEventData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/assigned-event-data`;
    return this.api.post(url, params);
  }

  /**
  * Sends a POST request to the /event/activated-event-data endpoint with an HMAC header and a Bearer token, returning the full HTTP response as an Observable.
  * @example
  * etActivatedEventData({ eventId: 123 })
  * // Observable<HttpResponse<any>> that emits the server response
  * @param {any} params - Request payload to be sent to the activated-event-data endpoint.
  * @returns {Observable<HttpResponse<any>>} Observable that emits the HttpResponse from the server.
  **/
  getActivatedEventData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/activated-event-data`;
    return this.api.post(url, params);
  }


  /**
  * Sends params to the save-all-activated-event-new endpoint after ensuring a valid token and attaching an HMAC; returns an observable of the full HTTP response.
  * @example
  * aveAllActivatedEventData({ eventId: 123, activated: true })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload to be posted to the API (payload will be HMAC-signed).
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the HttpResponse from the POST request.
  **/
  saveAllActivatedEventData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/save-all-activated-event-new`;
    return this.api.post(url, params);
  }

  /**
  * Send jsonparams to API endpoint to check all not-assigned compliance, using the current user's bearer token and HMAC header.
  * @example
  * heckAllNotAssignedCompliance({ eventId: 123 })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{jsonparams}} - JSON payload used for the compliance check (request body).
  * @returns {{Observable<HttpResponse<any>>}} Observable emitting the full HTTP response from the POST request.
  **/
  checkAllNotAssignedCompliance(jsonparams: any): Observable<HttpResponse<any>> {
    // const url = `${this.apiBaseUrl}/event/check-all-not-assigned-compliance`;

    const url = `${this.apiBaseUrl}/event/check-all-not-assigned-compliance-new`;
    return this.api.post(url, jsonparams);
  }

  /**
  * Sends a POST request to /event/save-all-activated-event-new with authorization and HMAC headers to save multiple activated events.
  * @example
  * aveAllActivatedEventNew({ "events": [...] })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{jsonparams}} - Request payload containing event data to be saved.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HTTP response from the server.
  **/
  saveAllActivatedEventNewOne(jsonparams: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/save-all-activated-event-new1`;
    return this.api.post(url, jsonparams);
  }

  saveAllActivatedEventNew(jsonparams: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/save-all-activated-event-new`;
    return this.api.post(url, jsonparams);
  }


  /**
  * Sends a request to fetch event data using a validated user token and an HMAC-signed payload.
  * @example
  * etEventData({ eventId: '123' })
  * Observable<HttpResponse<any>>
  * @param {any} params - The request payload containing event query parameters.
  * @returns {Observable<HttpResponse<any>>} Observable that emits the full HTTP response with the event data.
  **/
  getEventData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-event-data`;
    return this.api.post(url, params);
  }

  /**
  * Marks an event as "not applicable" by sending a POST to /event/save-event-not-applicable with HMAC-protected payload.
  * @example
  * aveEventNotApplicable({ eventId: 123 })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Payload object containing event data to be sent in the POST body.
  * @returns {{Observable<HttpResponse<any>>}} Returns an observable that emits the full HTTP response from the server.
  **/
  saveEventNotApplicable(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/save-event-not-applicable`;
    return this.api.post(url, params);
  }

  /**
  * Sends params to the backend POST /event/get-parent-event-data after ensuring a valid token and attaching an HMAC header, returning the full HTTP response as an observable.
  * @example
  * etParentEventData({ eventId: '123' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload to send to the get-parent-event-data endpoint.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HttpResponse from the server.
  **/
  getParentEventData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-parent-event-data`;
    return this.api.post(url, params);
  }

  /**
  * Retrieve sub-event data from the API, adding an authorization token and HMAC header before posting the params.
  * @example
  * etSubEventData({ eventId: '123' })
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload containing parameters to fetch sub-event data.
  * @returns {Observable<HttpResponse<any>>} Observable that emits the full HTTP response from the API.
  **/
  getSubEventData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-sub-event-data`;
    return this.api.post(url, params);
  }

  /**
  * Gets compliance data for an event by POSTing params to /event/get-compliance-data using a valid auth token and HMAC header, returning an observable of the full HTTP response.
  * @example
  * etComplianceData({ eventId: '123' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload object sent to the get-compliance-data endpoint.
  * @returns {{Observable<HttpResponse<any>>}} Observable emitting the full HttpResponse from the server.
  **/
  getComplianceData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-compliance-data`;
    return this.api.post(url, params);
  }

  /**
   * Retrieve compliance visibility and color for an event by calling the API while ensuring a valid token and HMAC header.
   * @example
   * etComplianceVisibilityAndColor({ eventId: '123' })
   * Observable<HttpResponse<any>> - emits HTTP response containing visibility and color data
   * @param {any} params - Payload parameters sent to the API to retrieve compliance visibility and color.
   * @returns {Observable<HttpResponse<any>>} Observable that emits the full HTTP response containing compliance visibility and color data.
   **/
  getComplianceVisibilityAndColor(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-compliance-visibility-and-color`;
    return this.api.post(url, params);
  }

  /**
  * Sends the provided payload to the /event/get-bind-internal-compliance endpoint after ensuring a valid token and attaching an HMAC header, returning the full HTTP response as an observable.
  * @example
  * etBindInternalCompliance({ exampleField: 'value' })
  * Observable<HttpResponse<any>> emitting the HTTP response from the server
  * @param {{any}} {{param}} - Request payload to be sent to the compliance endpoint.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the HttpResponse from the POST request.
  **/
  getBindInternalCompliance(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-bind-internal-compliance`;
    return this.api.post(url, params);
  }

  /**
  * Sends a POST request to add a child GRD internal compliance event, attaching Authorization and HMAC headers, and returns an observable of the full HTTP response.
  * @example
  * ddChildGridInternalCompliance({ id: 123, name: 'example' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload used as the POST body and to generate the X-Payload-HMAC header.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HttpResponse returned by the server.
  **/
  addChildGridInternalCompliance(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/add-child-grd-internal-compliance`;
    return this.api.post(url, params);
  }

  /**
  * Marks a child grid event as not applicable by sending a POST request to the API with authorization and HMAC headers.
  * @example
  * arkChildGridEventNotApplicable({ id: 123, reason: 'not relevant' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Payload object containing the child grid event identifier and any metadata required to mark it not applicable.
  * @returns {{Observable<HttpResponse<any>>}} Return observable that emits the full HTTP response from the API.
  **/
  markChildGridEventNotApplicable(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/mark-child-grd-event-not-applicable`;
    return this.api.post(url, params);
  }

  /**
  * Sends the given params to the backend to create or update child grid compliance, ensuring a valid token and attaching an HMAC header.
  * @example
  * hildGridUpdateOrSaveCompliance({ sampleKey: 'value' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Payload object containing compliance data to be sent to the server.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HttpResponse from the POST request.
  **/
  childGridUpdateOrSaveCompliance(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/child-grd-update-or-save-compliance`;
    return this.api.post(url, params);
  }

  /**
  * Fetches schedule dates from the backend (/event/get_schedule-dates) and returns an observable of the full HTTP response.
  * @example
  * etScheduleDates({ eventId: '123', startDate: '2026-01-01' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - The request payload object sent to the API (e.g., filters such as eventId and date range).
  * @returns {{Observable<HttpResponse<any>>}} Observable containing the full HTTP response with schedule date data.
  **/
  getScheduleDates(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get_schedule-dates`;
    return this.api.post(url, params);
  }

  /**
  * Sends a POST request to the backend endpoint to close a TN compliance schedule and returns the full HTTP response as an Observable.
  * @example
  * tnClosureCloseComplianceSchedule({ eventId: 123, scheduleId: 456 })
  * Observable<HttpResponse<any>> (emits the full HTTP response from the API)
  * @param {{any}} {{params}} - Request payload containing the data required to close the compliance schedule.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the complete HttpResponse returned by the server.
  **/
  btnClosureCloseComplianceSchedule(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/btn-closure-close-compliance-schedule`;
    return this.api.post(url, params);
  }

  /**
  * Sends a POST request to /event/get-intermediate-compliance with HMAC-authenticated headers and returns the full HTTP response as an Observable.
  * @example
  * etIntermediateCompliance({ eventId: 123 })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload containing parameters for intermediate compliance lookup.
  * @returns {{Observable<HttpResponse<any>>}} Observable that resolves to the full HTTP response containing intermediate compliance data.
  **/
  getIntermediateCompliance(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-intermediate-compliance`;
    return this.api.post(url, params);
  }

  /**
  * Sends params to /event/get-intermediate-compliance-visibility-and-color and returns the HTTP response Observable.
  * @example
  * etIntermediateComplianceVisibilityAndColor({ eventId: '123' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload which will be HMAC-signed and sent in the POST body.
  * @returns {{Observable<HttpResponse<any>>}} Observable wrapping the full HTTP response from the server.
  **/
  getIntermediateComplianceVisibilityAndColor(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/get-intermediate-compliance-visibility-and-color`;
    return this.api.post(url, params);
  }

  /**
  * Send a POST request to the /event/intermediate-grd-event-not-applicable endpoint with authorization and HMAC headers.
  * @example
  * ntermediateGridEventNotApplicable({ eventId: 123 })
  * Observable<HttpResponse<any>> (emits the full HTTP response from the server)
  * @param {{any}} {{params}} - Request payload to be sent in the POST body.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HTTP response from the server.
  **/
  intermediateGridEventNotApplicable(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/intermediate-grd-event-not-applicable`;
    return this.api.post(url, params);
  }

  /**
  * Send a POST request to /event/intermeditate-child-grd-update-compliance with the provided params, attaching an HMAC and bearer token, and return the full HTTP response as an observable.
  * @example
  * ntermeditateChildGridUpdateCompliance({ id: 123, status: 'ok' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload sent as JSON in the POST body and used to generate the X-Payload-HMAC header.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HttpResponse returned by the backend.
  **/
  intermeditateChildGridUpdateCompliance(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/intermeditate-child-grd-update-compliance`;
    return this.api.post(url, params);
  }

  /**
  * Retrieve all customer IDs associated with users by sending an authenticated POST to /common/get-all-user-customer-id.
  * @example
  * etAllUserCustomerId({ userId: '123' })
  * Observable<HttpResponse<any>> // e.g. HttpResponse with body: { customerIds: ['c1','c2'] }
  * @param {{any}} {{params}} - Request payload containing filters or identifiers used to fetch user customer IDs.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HTTP response containing the customer IDs.
  **/
  getAllUserCustomerId(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/common/get-all-user-customer-id`;
    return this.api.post(url, params);
  }


  /**
  * Sends a POST request to /event/save-compliance-list, including an HMAC header and Bearer token, and returns the full HTTP response as an Observable.
  * @example
  * aveComplianceList({ items: [{ id: 1, compliant: true }] })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Payload object containing the compliance list to save.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HttpResponse from the POST request.
  **/
  saveComplianceList(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/save-compliance-list`;
    return this.api.post(url, params);
  }

  /**
  * Send a POST request to the /dropdown/users-reassign-compliance endpoint after ensuring a valid token and adding an HMAC header.
  * @example
  * sersReassignCompliance({ userIds: [123], targetComplianceId: 'abc' })
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload sent to the users-reassign-compliance endpoint.
  * @returns {Observable<HttpResponse<any>>} Observable that emits the full HTTP response from the server.
  **/
  usersReassignCompliance(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/dropdown/users-reassign-compliance`;
    return this.api.post(url, params);
  }

  /**
  * Sends a short-notice event payload to the API and returns the HTTP response as an Observable.
  * @example
  * aveShortNotice({ title: 'Last minute gig', date: '2026-02-20' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Payload for the short-notice event to save.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits the full HttpResponse from the save-short-notice endpoint.
  **/
  saveShortNotice(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/event/save-short-notice`;
    return this.api.post(url, params);
  }
}
